/**
 * Consent state — the single gate in front of every non-essential tag.
 *
 * Design rule: FAIL CLOSED. Every unknown, corrupt, unreadable or stale state
 * resolves to "denied". A consent module that fails open produces no error, no
 * log line and no failing test in the app — it just silently fires the Meta
 * Pixel for users who never agreed. That is the failure mode this file exists
 * to prevent, so every catch block here returns denial rather than a fallback
 * to a previous value.
 *
 * Storage is dual on purpose:
 *   - localStorage  → the browser reads it to gate Pixel/GTM.
 *   - cookie (same key, URI-encoded JSON) → the SERVER reads it on the request
 *     that triggers a Meta CAPI event. Without the cookie the server has no way
 *     to know consent was refused and would keep sending server-side events
 *     after the user opted out — the exact leak §7 forbids.
 */

export const CONSENT_VERSION = 1;
export const CONSENT_KEY = 'wtfgo_consent';
export const CATEGORIES = ['necessary', 'analytics', 'marketing'];

// Cookie lifetime. Long enough not to nag, short enough that consent is
// re-confirmed periodically rather than assumed forever.
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180; // 180 days

/** Regions where consent must be OPT-IN (GDPR/UK GDPR + EEA + India DPDP). */
const OPT_IN_REGIONS = new Set([
  // EU member states
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // EEA non-EU
  'IS', 'LI', 'NO',
  // UK (both ISO code and the colloquial one, because callers pass both)
  'GB', 'UK',
  // India — DPDP Act is consent-first; marketing must not default on.
  'IN',
]);

/**
 * US states with comprehensive privacy laws. These are OPT-OUT regimes:
 * processing is lawful until the consumer objects, so analytics defaults on.
 * Marketing still defaults OFF because cross-context behavioural advertising is
 * precisely the "sale/share" these statutes give consumers a right to refuse,
 * and defaulting it on is the thing that draws enforcement.
 *
 * §7 is explicit that no single national rule covers every US state, so this is
 * an enumerated list. A US state that is NOT listed gets the permissive default
 * — that is a deliberate per-state decision, not a fallthrough. Adding a state
 * to this set is the only correct way to react to a new state law.
 */
const US_OPT_OUT_STATES = new Set([
  'CA', 'CO', 'CT', 'VA', 'UT', 'TX', 'OR', 'MT', 'DE', 'IA', 'NE', 'NH',
  'NJ', 'TN', 'MN', 'MD', 'IN', 'KY', 'RI',
]);

// ---------------------------------------------------------------------------
// storage primitives — every one of these swallows and denies, never rethrows
// ---------------------------------------------------------------------------

function safeGetItem(key) {
  try {
    // Safari private mode and locked-down enterprise profiles throw on access
    // to localStorage itself, not just on write.
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    // Quota exceeded / private mode. We keep the in-memory decision so the
    // current page behaves correctly; it simply will not survive a reload.
    return false;
  }
}

function writeCookie(state) {
  try {
    const value = encodeURIComponent(JSON.stringify(state));
    const secure = typeof window !== 'undefined' && window.location
      && window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${CONSENT_KEY}=${value}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
    return true;
  } catch (e) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// state
// ---------------------------------------------------------------------------

/**
 * A record is only valid consent if it is an object AND its version matches
 * exactly. A mismatch (older OR newer) means the record describes a different
 * set of purposes/vendors than the one currently in force, so it cannot stand
 * in for agreement to the current set. Treated as "no decision" → re-ask.
 */
function isValidRecord(record) {
  return (
    !!record &&
    typeof record === 'object' &&
    !Array.isArray(record) &&
    record.version === CONSENT_VERSION
  );
}

function readStored() {
  const raw = safeGetItem(CONSENT_KEY);
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    // Corrupt storage (truncated write, another app squatting the key) is
    // never interpreted charitably — it becomes "no decision".
    return null;
  }
  if (!isValidRecord(parsed)) return null;
  return normalise(parsed);
}

function normalise(record) {
  return {
    version: CONSENT_VERSION,
    analytics: record.analytics === true,
    marketing: record.marketing === true,
    ts: typeof record.ts === 'number' ? record.ts : Date.now(),
    region: typeof record.region === 'string' ? record.region : null,
  };
}

// In-memory mirror so the page still honours a decision when storage is
// unwritable (Safari private mode). `undefined` means "not yet loaded".
let cached;

function current() {
  if (cached === undefined) cached = readStored();
  return cached;
}

// ---------------------------------------------------------------------------
// subscribers
// ---------------------------------------------------------------------------

const listeners = new Set();

export function onConsentChange(cb) {
  if (typeof cb !== 'function') return () => {};
  listeners.add(cb);
  return function unsubscribe() {
    listeners.delete(cb);
  };
}

function notify(state) {
  // Copy first: a subscriber may unsubscribe during notification, and one
  // subscriber throwing must not stop the rest from learning consent changed —
  // a missed notification here means a tag keeps firing after withdrawal.
  Array.from(listeners).forEach((cb) => {
    try {
      cb(state);
    } catch (e) {
      /* isolate subscriber failures */
    }
  });
}

// ---------------------------------------------------------------------------
// cross-tab synchronisation
// ---------------------------------------------------------------------------

/**
 * FAILURE MODE THIS PREVENTS: a user withdraws consent in tab B. Tab A is still
 * open holding `cached` from before the withdrawal, and nothing ever
 * invalidates it — so tagGate/metaEvents in tab A keep firing the Meta Pixel for
 * someone who has already opted out, silently, until they reload the page. That
 * is post-withdrawal tracking: no error, no log, and exactly what a regulator
 * asks about. The 'storage' event is the only signal another tab gives us.
 */
function handleStorage(event) {
  // Only our key. Invalidating on every key would let an unrelated app's write
  // wipe a valid in-memory decision (which fails closed and re-prompts a user
  // who already answered) — over-reacting is its own bug.
  if (!event || event.key !== CONSENT_KEY) return;

  // Force a re-read from storage rather than trusting event.newValue: the value
  // still has to pass version + shape validation, and readStored() is the single
  // place that decides what counts as consent.
  cached = undefined;
  const state = current();
  notify(state ? { ...state } : null);
}

// Module-scope flag so importing this module from several entry points (or a
// bundler evaluating it twice) cannot register two listeners and double-fire
// every subscriber.
let storageListenerAttached = false;

function attachStorageListener() {
  // Guard for SSR/node: there is no window, and throwing here would take down
  // any server render that merely imports the consent module.
  if (typeof window === 'undefined' || !window.addEventListener) return;
  if (storageListenerAttached) return;
  try {
    window.addEventListener('storage', handleStorage);
    storageListenerAttached = true;
  } catch (e) {
    /* nothing we can do; consent still works within this tab */
  }
}

attachStorageListener();

// ---------------------------------------------------------------------------
// public API
// ---------------------------------------------------------------------------

/** @returns {object|null} the current record, or null when no decision exists. */
export function getConsent() {
  const state = current();
  return state ? { ...state } : null;
}

/** True once the user has made any decision (including a full refusal). */
export function hasDecision() {
  return current() !== null;
}

export function setConsent({ analytics = false, marketing = false, region = null } = {}) {
  const state = normalise({ analytics, marketing, region, ts: Date.now() });
  cached = state;
  safeSetItem(CONSENT_KEY, JSON.stringify(state));
  writeCookie(state);
  notify({ ...state });
  return { ...state };
}

/**
 * Withdrawal keeps the *record* (so we do not re-prompt someone who just said
 * no) while zeroing the permissions. Deleting the record entirely would look
 * identical to "never asked" and the banner would nag them again.
 */
export function withdraw() {
  const previous = current();
  return setConsent({ analytics: false, marketing: false, region: previous ? previous.region : null });
}

export function isAllowed(category) {
  if (category === 'necessary') return true;      // strictly necessary, always on
  if (!CATEGORIES.includes(category)) return false; // unknown category → denied
  const state = current();
  if (!state) return false;                        // no decision → DENIED
  return state[category] === true;
}

export function defaultsForRegion(region) {
  if (typeof region !== 'string' || !region) {
    // Unknown location is treated as the strictest jurisdiction we serve.
    return { analytics: false, marketing: false };
  }
  const code = region.trim().toUpperCase();

  if (code.startsWith('US-') || code.startsWith('US_')) {
    const stateCode = code.slice(3);
    if (US_OPT_OUT_STATES.has(stateCode)) {
      return { analytics: true, marketing: false };
    }
    // A US state with no comprehensive statute. Explicitly permissive — not a
    // fallthrough from the opt-out states above (§7: no single national rule).
    return { analytics: true, marketing: true };
  }

  if (OPT_IN_REGIONS.has(code)) return { analytics: false, marketing: false };
  if (code === 'US') return { analytics: true, marketing: true };

  // Any country we have not explicitly assessed defaults to fully denied.
  return { analytics: false, marketing: false };
}

export default {
  CONSENT_VERSION,
  CONSENT_KEY,
  CATEGORIES,
  getConsent,
  setConsent,
  hasDecision,
  isAllowed,
  withdraw,
  defaultsForRegion,
  onConsentChange,
};
